/**
 * adaptiveViewportTexture.ts — a scene-color node whose copy destination
 * MATCHES the bound render target's texel type.
 *
 * r182's `viewportSharedTexture` copies the current framebuffer into ONE
 * module-level `FramebufferTexture` fixed at UnsignedByteType. Inside an HDR
 * post pipeline the scene pass renders into rgba16float, and WebGPU requires
 * copy source/destination formats to be identical — so the copy fails every
 * frame ("Source and destination formats do not match. rgba16float
 * bgra8unorm") and refraction/underwater sampling reads a dead texture. This
 * node keeps one shared destination per render target and stamps its
 * `internalFormat` from that target before use — NOT just `type`, because on
 * WebGPU a FramebufferTexture's allocation ignores `type` (hardwired to the
 * canvas format outside the copy call, whose own allocation attempt is dead
 * on arrival behind the version early-return) and `Textures.updateTexture`
 * clobbers `type` besides. `internalFormat` wins in every allocation path, so
 * the same material works both inside an HDR pipeline and direct-to-canvas.
 */
import * as THREE from 'three/webgpu';
import { nodeObject, screenUV } from 'three/tsl';

/** One shared default destination (canvas path) + per-render-target textures —
 *  mirrors ViewportSharedTextureNode's sharing so multiple water materials
 *  reuse the same textures instead of allocating per node. */
type AdaptiveTex = THREE.FramebufferTexture & {
  __typeDirty?: boolean;
  __wantType?: THREE.TextureDataType;
  __wantFormat?: string | null;
};
let sharedDefault: THREE.FramebufferTexture | null = null;
const sharedPerTarget = new WeakMap<THREE.RenderTarget, AdaptiveTex>();
const _sizeScratch = new THREE.Vector2();

/**
 * The wgpu format three's backend derives for an uncompressed RGBA
 * render-target texture — what `getCurrentColorFormat(renderTarget)` will
 * report for the copy SOURCE. Stamped as the destination's `internalFormat`
 * because that is the ONE input that wins in EVERY allocation path
 * (`texture.internalFormat || options.format || getFormat(...)`): for a
 * FramebufferTexture the backend otherwise hardwires the format to
 * `getPreferredCanvasFormat()` (bgra8unorm) when allocation happens via the
 * sampled-binding path — and `Textures.updateTexture`'s version early-return
 * means whoever allocates first wins forever. `texture.type` is IGNORED for
 * the allocation and even CLOBBERED by `Textures.updateTexture` from the
 * public `getRenderTarget()`.
 */
function gpuFormatForReference(refTex: THREE.Texture): string | null {
  switch (refTex.type) {
    case THREE.HalfFloatType: return 'rgba16float';
    case THREE.FloatType: return 'rgba32float';
    case THREE.UnsignedByteType:
      return refTex.colorSpace === THREE.SRGBColorSpace ? 'rgba8unorm-srgb' : 'rgba8unorm';
    default: return null; // unknown layout — leave three's own derivation in charge
  }
}

/**
 * The render target the copy will ACTUALLY source from — resolved the same way
 * r182's Renderer.copyFramebufferToTexture resolves it. INSIDE a render the
 * active context's target is the WHOLE truth: non-null = that target's
 * texture, null = the canvas itself. Falling through a null context target to
 * the internal frame-buffer target picks an rgba16float destination for a
 * bgra8unorm canvas source (the mirrored live mismatch: water drawn in a
 * direct-to-canvas pass such as the underwater veil). The fallback chain runs
 * only OUTSIDE a render pass, mirroring the renderer's own else-branch: the
 * public target, then the INTERNAL frame-buffer target (the rgba16float
 * target the tone-mapped canvas path renders through — `getRenderTarget()`
 * returns null there, which is why the base class can never adapt that path).
 */
function resolveEffectiveTarget(renderer: THREE.WebGPURenderer): THREE.RenderTarget | null {
  const r = renderer as unknown as {
    _currentRenderContext?: { renderTarget?: THREE.RenderTarget | null } | null;
    _getFrameBufferTarget?: () => THREE.RenderTarget | null;
    getRenderTarget(): THREE.RenderTarget | null;
  };
  try {
    const ctx = r._currentRenderContext;
    if (ctx) return ctx.renderTarget ?? null;
    return r.getRenderTarget() ?? r._getFrameBufferTarget?.() ?? null;
  } catch {
    return r.getRenderTarget();
  }
}

class AdaptiveViewportTextureNode extends THREE.ViewportTextureNode {
  static get type(): string {
    return 'AdaptiveViewportTextureNode';
  }

  constructor(uvNode?: THREE.Node, levelNode: THREE.Node | null = null) {
    if (sharedDefault === null) {
      sharedDefault = new THREE.FramebufferTexture(1, 1);
      sharedDefault.minFilter = THREE.LinearMipmapLinearFilter;
    }
    super(uvNode, levelNode, sharedDefault);
  }

  override updateReference(frame: unknown): THREE.Texture {
    // The SAMPLED texture must be the SAME one the copy fills — resolve the
    // reference with the effective target, not the (often-null) public one.
    const renderer = (frame as { renderer: THREE.WebGPURenderer }).renderer;
    this.value = this.getTextureForReference(resolveEffectiveTarget(renderer), renderer);
    return this.value as THREE.Texture;
  }

  override updateBefore(frame: unknown): void {
    // FULL replacement of the base updateBefore (never call super) — live-
    // profiled on real WebGPU 2026-07-31: the old override sized `this.value`
    // to the DRAWING BUFFER unconditionally, but inside the post pipeline the
    // bound target is the INTERNAL render resolution (TAAU renders below
    // display res). The two layers then fought: per-frame reallocation churn,
    // Dawn "copy range touches outside of texture" (copySize = display dims vs
    // a pass-sized texture), "formats do not match", "RenderPassEncoder was
    // already ended" — a validation-error storm with multi-second stalls near
    // any water surface. Rules enforced here:
    //   1. Size comes from the SAME context the base would use — the bound
    //      render target when one is set, else the drawing buffer.
    //   2. Size AND format adapt on the SAME texture the copy will use
    //      (getTextureForReference(renderTarget)).
    //   3. A frame that reallocates (size or type changed) NEVER also copies —
    //      refraction samples last frame's texture once (invisible).
    const renderer = (frame as { renderer: THREE.WebGPURenderer }).renderer;
    // A copy issued OUTSIDE an active render (pipeline pre-warm /
    // compileAsync) sources a stale swapchain texture through a throwaway
    // encoder — garbage on every backend. Sample last frame's copy instead.
    const live = (renderer as unknown as { _currentRenderContext?: object | null })._currentRenderContext;
    if (!live) return;
    const renderTarget = resolveEffectiveTarget(renderer);
    if (renderTarget === null) renderer.getDrawingBufferSize(_sizeScratch);
    else _sizeScratch.set(renderTarget.width, renderTarget.height);

    const tex = this.getTextureForReference(renderTarget, renderer) as AdaptiveTex;
    const img = tex.image as { width: number; height: number };
    let realloc = tex.__typeDirty === true;
    tex.__typeDirty = false;
    if (img.width !== _sizeScratch.width || img.height !== _sizeScratch.height) {
      img.width = _sizeScratch.width;
      img.height = _sizeScratch.height;
      realloc = true;
    }
    if (realloc) {
      tex.needsUpdate = true;
      return;
    }
    const prevMips = tex.generateMipmaps;
    tex.generateMipmaps = this.generateMipmaps;
    try {
      renderer.copyFramebufferToTexture(tex);
    } catch { /* backend refused this context — sample last frame's copy */ }
    tex.generateMipmaps = prevMips;
  }

  override getTextureForReference(reference: THREE.RenderTarget | null = null, renderer?: THREE.WebGPURenderer): THREE.Texture {
    // null = the true canvas framebuffer (no internal HDR intermediate) —
    // three allocates the untouched default at getPreferredCanvasFormat(),
    // which is exactly what context.getCurrentTexture() reports. No adapting.
    const base = sharedDefault as THREE.FramebufferTexture;
    if (reference === null) return base;
    let tex = sharedPerTarget.get(reference);
    if (!tex) {
      // NOT base.clone(): Texture.copy() shares the Source, so every clone and
      // the default would fight over ONE {width,height} image — per-target
      // sizes must be independent (TAAU pass target ≠ drawing buffer).
      tex = new THREE.FramebufferTexture(1, 1) as AdaptiveTex;
      tex.minFilter = base.minFilter;
      tex.magFilter = base.magFilter;
      tex.__typeDirty = true; // first frame allocates; the copy waits one frame
      sharedPerTarget.set(reference, tex);
    }
    const refTex = reference.textures?.[0] ?? reference.texture;
    const wantType = refTex?.type ?? THREE.UnsignedByteType;
    // internalFormat is WebGPU-only vocabulary — the WebGL backend derives its
    // allocation from texture.type and would choke on a wgpu format string.
    const isWebGPU = (renderer as unknown as { backend?: { isWebGPUBackend?: boolean } } | undefined)?.backend?.isWebGPUBackend === true;
    const wantFormat = isWebGPU && refTex ? gpuFormatForReference(refTex) : null;
    // Dirty-check against OUR bookkeeping, never tex.type — Textures.updateTexture
    // rewrites a FramebufferTexture's type from the public getRenderTarget() on
    // every version bump, so keying on tex.type re-triggers forever.
    if (tex.__wantType !== wantType || tex.__wantFormat !== wantFormat) {
      tex.__wantType = wantType;
      tex.__wantFormat = wantFormat;
      tex.type = wantType;
      if (isWebGPU) (tex as { internalFormat: string | null }).internalFormat = wantFormat;
      tex.needsUpdate = true; // reallocate the GPU texture in the matching format
      tex.__typeDirty = true; // updateBefore must skip this frame's copy
    }
    return tex;
  }
}

/** Drop-in for `viewportSharedTexture(uv)` — same sampling, format-safe copy.
 *  (Return type inferred: ShaderNodeObject isn't re-exported by the three
 *  build d.ts entries.) */
export function adaptiveSceneColor(uvNode?: THREE.Node) {
  return nodeObject(new AdaptiveViewportTextureNode(uvNode ?? (screenUV as unknown as THREE.Node)));
}
